import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  NutritionPlan,
  Meal,
  PlanStore
} from '../types';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';

const RECIPE_SOURCE = 'ai_v4';

let dayCalculationCache: {
  planId: string | null;
  lastCalculated: number;
  result: number;
} = {
  planId: null,
  lastCalculated: 0,
  result: 1
};

const CACHE_TTL = 60000;

export const usePlanStore = create<PlanStore>()(
  persist(
    (set, get) => ({
      activePlan: null,
      activeDays: [],
      activeMeals: [],
      activeCurrentDay: 1,
      viewingPlan: null,
      viewingDays: [],
      viewingMeals: [],
      viewingCurrentDay: 1,
      planHistory: [],
      isGenerating: false,
      isLoading: false,
      generationProgress: 0,
      currentDay: 0,
      error: null,
      hasLoadedInitial: false, // NEW

      showPlanCompletionModal: false,
      planCompletionData: null,

      setActivePlan: (plan) => {
        set({ activePlan: plan });
        if (plan) {
          const activeCurrentDay = get().calculateCurrentDay(plan);
          set({ activeCurrentDay, currentDay: activeCurrentDay });
        } else {
          set({ activeCurrentDay: 1, currentDay: 0 });
        }
      },
      setPlanDays: (days) => set({ activeDays: days }),
      setMeals: (meals) => set({ activeMeals: meals }),
      setIsGenerating: (generating) => set({ isGenerating: generating }),
      setGenerationProgress: (progress) => set({ generationProgress: progress }),
      setCurrentDay: (day) => set({ currentDay: day, activeCurrentDay: day }),
      setError: (error) => set({ error }),

      calculateCurrentDay: (plan: NutritionPlan) => {
        if (!plan) return 1;

        const now = Date.now();

        if (
          dayCalculationCache.planId === plan.id &&
          (now - dayCalculationCache.lastCalculated) < CACHE_TTL
        ) {
          return dayCalculationCache.result;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (plan.is_completed) {
          dayCalculationCache = {
            planId: plan.id,
            lastCalculated: now,
            result: plan.duration_days
          };
          return plan.duration_days;
        }

        const startDate = new Date(plan.plan_start_date);
        startDate.setHours(0, 0, 0, 0);

        const effectiveEndDate = plan.actual_end_date
          ? new Date(plan.actual_end_date)
          : new Date(startDate.getTime() + (plan.duration_days + (plan.total_inactive_days || 0)) * 24 * 60 * 60 * 1000);

        effectiveEndDate.setHours(0, 0, 0, 0);

        if (today < startDate) {
          dayCalculationCache = { planId: plan.id, lastCalculated: now, result: 1 };
          return 1;
        }

        if (today > effectiveEndDate) {
          dayCalculationCache = {
            planId: plan.id,
            lastCalculated: now,
            result: plan.duration_days
          };
          return plan.duration_days;
        }

        let daysSinceStart: number;

        if (!plan.is_active && plan.last_deactivated_at) {
          const deactivatedDate = new Date(plan.last_deactivated_at);
          deactivatedDate.setHours(0, 0, 0, 0);
          daysSinceStart = Math.floor((deactivatedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        } else {
          const totalDaysPassed = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          daysSinceStart = totalDaysPassed - (plan.total_inactive_days || 0);
        }

        const currentDay = daysSinceStart + 1;
        const result = Math.max(1, Math.min(currentDay, plan.duration_days));

        dayCalculationCache = {
          planId: plan.id,
          lastCalculated: now,
          result
        };

        return result;
      },

      updateActiveCurrentDay: () => {
        const { activePlan } = get();
        if (activePlan) {
          const activeCurrentDay = get().calculateCurrentDay(activePlan);
          set({ activeCurrentDay, currentDay: activeCurrentDay });
        }
      },

      updateViewingCurrentDay: () => {
        const { viewingPlan } = get();
        if (viewingPlan) {
          const viewingCurrentDay = get().calculateCurrentDay(viewingPlan);
          set({ viewingCurrentDay });
        }
      },

      loadActivePlan: async (userId: string) => {
        // FIXED: Concurrency guard - prevent multiple components from firing this at once
        if (get().isLoading) return;

        try {
          set({ isLoading: true, error: null });

          const { data: plans, error: planError } = await supabase
            .from('nutrition_plans')
            .select('*')
            .eq('user_id', userId)
            .eq('is_active', true)
            .order('created_at', { ascending: false });

          if (planError) throw planError;

          if (!plans || plans.length === 0) {
            set({
              activePlan: null,
              activeDays: [],
              activeMeals: [],
              activeCurrentDay: 1,
              viewingPlan: null,
              viewingDays: [],
              viewingMeals: [],
              viewingCurrentDay: 1,
              currentDay: 0,
              isLoading: false,
              hasLoadedInitial: true, // Mark as loaded so we don't infinitely retry
              error: null
            });
            return;
          }

          const plan = plans[0];

          const { data: days, error: daysError } = await supabase
            .from('plan_days')
            .select('*')
            .eq('plan_id', plan.id)
            .order('day_number', { ascending: true });

          if (daysError) throw daysError;

          const dayIds = days?.map(d => d.id) || [];

          if (dayIds.length === 0) {
            const activeCurrentDay = get().calculateCurrentDay(plan);
            set({
              activePlan: plan,
              activeDays: [],
              activeMeals: [],
              activeCurrentDay,
              viewingPlan: plan,
              viewingDays: [],
              viewingMeals: [],
              viewingCurrentDay: activeCurrentDay,
              currentDay: activeCurrentDay,
              isLoading: false,
              hasLoadedInitial: true, // Mark as loaded
            });
            return;
          }

          const { data: meals, error: mealsError } = await supabase
            .from('meals')
            .select(`
              *,
              recipes (
                id, source, title, image_url, calories, protein, carbs, fat,
                ingredients, instructions, description, prep_time, cook_time,
                total_time, yield, cuisine_type, dish_type, tags, source_url
              )
            `)
            .in('plan_day_id', dayIds)
            .order('meal_order', { ascending: true });

          if (mealsError) throw mealsError;

          const enrichedMeals = meals?.map(meal => {
            const dayData = days?.find(d => d.id === meal.plan_day_id);
            let recipeData = undefined;

            if (meal.recipes) {
              if (meal.recipes.source === RECIPE_SOURCE) {
                recipeData = {
                  ...meal.recipes,
                  meal_type: meal.meal_type,
                  meal_order: meal.meal_order,
                  day_number: dayData?.day_number
                };
              }
            }

            return {
              ...meal,
              day: dayData?.day_number,
              recipes: recipeData
            };
          }) || [];

          const activeCurrentDay = get().calculateCurrentDay(plan);

          set({
            activePlan: plan,
            activeDays: days || [],
            activeMeals: enrichedMeals,
            activeCurrentDay,
            viewingPlan: plan,
            viewingDays: days || [],
            viewingMeals: enrichedMeals,
            viewingCurrentDay: activeCurrentDay,
            currentDay: activeCurrentDay,
            isLoading: false,
            hasLoadedInitial: true, // Mark as loaded on success
            error: null
          });

        } catch (error: any) {
          console.error('[planStore] Failed to load active plan:', error);
          set({
            error: error.message,
            isLoading: false,
            hasLoadedInitial: true // Mark as loaded even on error to stop infinite retry loops
          });
        }
      },

      loadPlanHistory: async (userId: string) => {
        try {
          const { data: plans, error } = await supabase
            .from('nutrition_plans')
            .select('id, title, goal, plan_start_date, duration_days, is_active, is_completed, completed_at, actual_end_date, total_inactive_days, created_at')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

          if (error) throw error;

          set({
            planHistory: plans || [],
            error: null
          });
        } catch (error: any) {
          console.error('[planStore] Failed to load plan history:', error);
          set({ error: error.message });
        }
      },

      setViewingPlan: async (planId: string) => {
        try {
          set({ isLoading: true, error: null });

          const { data: plan, error: planError } = await supabase
            .from('nutrition_plans')
            .select('*')
            .eq('id', planId)
            .single();

          if (planError) throw planError;

          const { data: days, error: daysError } = await supabase
            .from('plan_days')
            .select('*')
            .eq('plan_id', plan.id)
            .order('day_number', { ascending: true });

          if (daysError) throw daysError;

          const dayIds = days?.map(d => d.id) || [];
          const { data: meals, error: mealsError } = await supabase
            .from('meals')
            .select(`
              *,
              recipes (
                id, source, title, image_url, calories, protein, carbs, fat,
                ingredients, instructions, description, prep_time, cook_time,
                total_time, yield, cuisine_type, dish_type, tags, source_url
              )
            `)
            .in('plan_day_id', dayIds)
            .order('meal_order', { ascending: true });

          if (mealsError) throw mealsError;

          const enrichedMeals = meals?.map(meal => {
            const dayData = days?.find(d => d.id === meal.plan_day_id);
            let recipeData = undefined;

            if (meal.recipes) {
              if (meal.recipes.source === RECIPE_SOURCE) {
                recipeData = {
                  ...meal.recipes,
                  meal_type: meal.meal_type,
                  meal_order: meal.meal_order,
                  day_number: dayData?.day_number
                };
              }
            }

            return {
              ...meal,
              day: dayData?.day_number,
              recipes: recipeData
            };
          }) || [];

          const viewingCurrentDay = get().calculateCurrentDay(plan);

          set({
            viewingPlan: plan,
            viewingDays: days || [],
            viewingMeals: enrichedMeals,
            viewingCurrentDay,
            isLoading: false,
            error: null
          });

        } catch (error: any) {
          console.error('[planStore] Failed to set viewing plan:', error);
          set({
            error: error.message,
            isLoading: false
          });
        }
      },

      activateViewingPlan: async () => {
        const { viewingPlan, activePlan } = get();
        if (!viewingPlan || !activePlan?.user_id) return;

        set({ isLoading: true });
        try {
          await supabase
            .from('nutrition_plans')
            .update({ is_active: false })
            .eq('user_id', activePlan.user_id);

          await supabase
            .from('nutrition_plans')
            .update({ is_active: true })
            .eq('id', viewingPlan.id);

          set({ isLoading: false });

          await get().loadActivePlan(activePlan.user_id);
          await get().loadPlanHistory(activePlan.user_id);

        } catch (error: any) {
          console.error('[planStore] Failed to activate plan:', error);
          set({ error: error.message, isLoading: false });
        }
      },

      getDayMeals: (dayNumber: number) => {
        const { activeDays, activeMeals } = get();
        const day = activeDays.find(d => d.day_number === dayNumber);
        if (!day) return [];
        return activeMeals.filter(m => m.plan_day_id === day.id);
      },

      getTodaysMeals: (date: Date) => {
        const { activeDays, activeMeals, activePlan } = get();
        if (!activePlan || activeDays.length === 0) return [];

        const selectedDate = new Date(date);
        selectedDate.setHours(0, 0, 0, 0);
        const selectedDateStr = selectedDate.toISOString().split('T')[0];

        let day = activeDays.find(d => d.date === selectedDateStr);
        if (!day) {
          const startDate = new Date(activePlan.created_at);
          startDate.setHours(0, 0, 0, 0);
          const daysPassed = Math.floor((selectedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          const dayNumber = daysPassed + 1;

          if (dayNumber >= 1 && dayNumber <= activePlan.duration_days) {
            day = activeDays.find(d => d.day_number === dayNumber);
          }
        }
        if (!day) return [];
        return activeMeals.filter(m => m.plan_day_id === day.id);
      },

      toggleMealComplete: async (mealId: string) => {
        try {
          const { activeMeals, activeDays, viewingMeals } = get();
          const toggleInList = (list: Meal[]) =>
            list.map(m => m.id === mealId ? { ...m, is_completed: !m.is_completed } : m);

          const meal = activeMeals.find(m => m.id === mealId) || viewingMeals.find(m => m.id === mealId);
          if (!meal) throw new Error('Meal not found');

          const newCompletedStatus = !meal.is_completed;
          set({ activeMeals: toggleInList(activeMeals), viewingMeals: toggleInList(viewingMeals) });

          const { error: mealError } = await supabase
            .from('meals')
            .update({
              is_completed: newCompletedStatus,
              completed_at: newCompletedStatus ? new Date().toISOString() : null
            })
            .eq('id', mealId);

          if (mealError) throw mealError;

          const dayMeals = activeMeals.map(m =>
            m.id === mealId ? { ...m, is_completed: newCompletedStatus } : m
          ).filter(m => m.plan_day_id === meal.plan_day_id);

          const allMealsCompleted = dayMeals.length > 0 && dayMeals.every(m => m.is_completed);
          const planDay = activeDays.find(d => d.id === meal.plan_day_id);

          if (allMealsCompleted && planDay && !planDay.is_completed) {
            const { error: dayError } = await supabase
              .from('plan_days')
              .update({ is_completed: true })
              .eq('id', meal.plan_day_id);

            if (!dayError) {
              set({
                activeDays: activeDays.map(d =>
                  d.id === meal.plan_day_id ? { ...d, is_completed: true } : d
                )
              });
            }
          }

          if (!allMealsCompleted && planDay && planDay.is_completed) {
            const { error: dayError } = await supabase
              .from('plan_days')
              .update({ is_completed: false })
              .eq('id', meal.plan_day_id);

            if (!dayError) {
              set({
                activeDays: activeDays.map(d =>
                  d.id === meal.plan_day_id ? { ...d, is_completed: false } : d
                )
              });
            }
          }

          if (newCompletedStatus) {
            const state = get();
            const { activePlan, activeCurrentDay } = state;
            const isLastDay = activeCurrentDay === activePlan?.duration_days;

            if (isLastDay) {
              const todaysMeals = get().getTodaysMeals(new Date());
              const allMealsComplete = todaysMeals.every(m => m.is_completed);

              if (allMealsComplete) {
                setTimeout(() => {
                  const authState = useAuthStore.getState();
                  const user = authState.user;

                  if (!user || !activePlan) return;

                  const startWeight = activePlan.start_weight || user.currentWeight || 0;
                  const currentWeight = user.currentWeight || startWeight;
                  const weightChange = currentWeight - startWeight;

                  const goalAchieved = user.goal === 'lose'
                    ? currentWeight <= (user.targetWeight || currentWeight)
                    : currentWeight >= (user.targetWeight || currentWeight);

                  set({
                    showPlanCompletionModal: true,
                    planCompletionData: {
                      planId: activePlan.id,
                      daysCompleted: activeCurrentDay,
                      totalDays: activePlan.duration_days || 30,
                      weightChange,
                      goalAchieved
                    }
                  });
                }, 3000);
              }
            }
          }

        } catch (error: any) {
          console.error('Failed to toggle meal completion:', error);
          set({ error: error.message });
        }
      },

      subscribeToPlanUpdates: (userId: string) => {
        const channel = supabase
          .channel('plan-updates')
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'nutrition_plans',
            filter: `user_id=eq.${userId}`
          }, (payload) => {
            const updatedPlan = payload.new as NutritionPlan;
            set({ activePlan: updatedPlan });
            get().updateActiveCurrentDay();
          })
          .on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'meals'
          }, (payload) => {
            const updatedMeal = payload.new as Meal;
            const { activeMeals, viewingMeals } = get();

            set({
              activeMeals: activeMeals.map(m => m.id === updatedMeal.id ? updatedMeal : m),
              viewingMeals: viewingMeals.map(m => m.id === updatedMeal.id ? updatedMeal : m)
            });
          })
          .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'meals'
          }, (payload) => {
            const newMeal = payload.new as Meal;
            const { activeMeals, viewingMeals } = get();

            set({
              activeMeals: [...activeMeals, newMeal],
              viewingMeals: [...viewingMeals, newMeal]
            });
          })
          .subscribe();

        return () => {
          supabase.removeChannel(channel);
        };
      },

      clearPlan: () => set({
        activePlan: null,
        activeDays: [],
        activeMeals: [],
        activeCurrentDay: 1,
        viewingPlan: null,
        viewingDays: [],
        viewingMeals: [],
        viewingCurrentDay: 1,
        planHistory: [],
        isGenerating: false,
        generationProgress: 0,
        currentDay: 0,
        error: null,
        showPlanCompletionModal: false,
        planCompletionData: null,
        hasLoadedInitial: false // Reset on logout
      }),

      closePlanCompletionModal: () => {
        set({
          showPlanCompletionModal: false,
          planCompletionData: null
        });
      }
    }),

    {
      name: 'nutribot-plan-storage',
      partialize: (state) => ({
        activeCurrentDay: state.activeCurrentDay,
        currentDay: state.currentDay,
        activePlanId: state.activePlan?.id || null,
        // We explicitly DO NOT partialize hasLoadedInitial so it resets on hard refresh
      }),
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          if (typeof window !== 'undefined') {
            localStorage.setItem(name, JSON.stringify(value));
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);